import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  Search,
  Upload,
  FileText,
  Loader2,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import {
  listDocuments,
  searchKnowledge,
  uploadDocument,
  type KnowledgeSearchHit,
} from "@/api/endpoints/knowledge";

// Knowledge page — proxies to OMER core RAG.
// Three sections: upload form, document list, semantic search.

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

export default function Knowledge() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<KnowledgeSearchHit[] | null>(
    null,
  );

  const docsQuery = useQuery({
    queryKey: ["knowledge", "documents"],
    queryFn: listDocuments,
    refetchInterval: 30_000,
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadDocument(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "documents"] });
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
  });

  const searchMut = useMutation({
    mutationFn: (q: string) => searchKnowledge(q, 10),
    onSuccess: (data) => setSearchResults(data.results ?? []),
  });

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadMut.mutate(file);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) searchMut.mutate(query.trim());
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <BookOpen className="h-7 w-7 text-blue-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Knowledge</h1>
          <p className="text-sm text-gray-400">
            Documents indexes via OMER (BGE-M3 embeddings). Upload, recherche
            semantique, listing.
          </p>
        </div>
      </div>

      {/* Upload */}
      <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-200">
          <Upload className="h-4 w-4" /> Upload de document
        </h2>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.txt,.md,.docx,.xlsx"
          onChange={handleUpload}
          disabled={uploadMut.isPending}
          className="block w-full text-sm text-gray-400 file:mr-3 file:rounded file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-500 disabled:opacity-50"
        />
        <p className="mt-1 text-xs text-gray-500">
          Formats acceptes: PDF, TXT, MD, DOCX, XLSX. Max 50 MB.
        </p>
        {uploadMut.isPending && (
          <p className="mt-2 flex items-center gap-2 text-sm text-blue-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Indexation en cours...
          </p>
        )}
        {uploadMut.isSuccess && (
          <p className="mt-2 flex items-center gap-2 text-sm text-emerald-400">
            <CheckCircle2 className="h-4 w-4" /> Indexe :{" "}
            {uploadMut.data?.chunks ?? 0} chunks
          </p>
        )}
        {uploadMut.isError && (
          <p className="mt-2 flex items-center gap-2 text-sm text-red-400">
            <AlertTriangle className="h-4 w-4" />{" "}
            {(uploadMut.error as Error).message}
          </p>
        )}
      </section>

      {/* Search */}
      <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-200">
          <Search className="h-4 w-4" /> Recherche semantique
        </h2>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Posez une question..."
            className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-blue-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!query.trim() || searchMut.isPending}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {searchMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Rechercher"
            )}
          </button>
        </form>
        {searchMut.isError && (
          <p className="mt-2 flex items-center gap-2 text-sm text-red-400">
            <AlertTriangle className="h-4 w-4" />{" "}
            {(searchMut.error as Error).message}
          </p>
        )}
        {searchResults !== null && (
          <div className="mt-4 space-y-2">
            {searchResults.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun resultat.</p>
            ) : (
              searchResults.map((hit, i) => (
                <div
                  key={i}
                  className="rounded border border-gray-800 bg-gray-950 p-3"
                >
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {(hit.metadata?.filename as string) ?? "(sans nom)"}
                    </span>
                    {hit.score !== undefined && (
                      <span className="font-mono">
                        score={hit.score.toFixed(3)}
                      </span>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-gray-300">
                    {hit.content ?? hit.text ?? ""}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* Documents list */}
      <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-200">
          <FileText className="h-4 w-4" /> Documents indexes
          {docsQuery.data && (
            <span className="text-gray-500">
              ({docsQuery.data.documents.length})
            </span>
          )}
        </h2>
        {docsQuery.isLoading && (
          <p className="text-sm text-gray-500">Chargement...</p>
        )}
        {docsQuery.error && (
          <p className="flex items-center gap-2 text-sm text-red-400">
            <AlertTriangle className="h-4 w-4" />{" "}
            {(docsQuery.error as Error).message}
          </p>
        )}
        {docsQuery.data && docsQuery.data.documents.length === 0 && (
          <p className="text-sm text-gray-500">Aucun document indexe.</p>
        )}
        {docsQuery.data && docsQuery.data.documents.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4 font-medium">Fichier</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Taille</th>
                  <th className="py-2 pr-4 font-medium">Chunks</th>
                  <th className="py-2 pr-4 font-medium">Statut</th>
                  <th className="py-2 font-medium">Indexe le</th>
                </tr>
              </thead>
              <tbody>
                {docsQuery.data.documents.map((d) => (
                  <tr
                    key={d.id}
                    className="border-b border-gray-800/50 text-gray-300"
                  >
                    <td className="py-2 pr-4 font-mono text-xs">{d.filename}</td>
                    <td className="py-2 pr-4 text-xs text-gray-500">
                      {d.content_type}
                    </td>
                    <td className="py-2 pr-4">{formatBytes(d.file_size)}</td>
                    <td className="py-2 pr-4">{d.chunk_count}</td>
                    <td className="py-2 pr-4">
                      <span className="rounded bg-emerald-900/40 px-2 py-0.5 text-xs text-emerald-300">
                        {d.status}
                      </span>
                    </td>
                    <td className="py-2 text-xs text-gray-500">
                      {formatDate(d.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
